import React, { useState, useEffect } from 'react';
import logo from '../assets/logo.png';
import { useTheme } from '../ThemeContext';

const SunIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <circle cx="12" cy="12" r="5" />
    <path strokeLinecap="round" d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
  </svg>
);

const MoonIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
  </svg>
);

export const ThemeToggle = ({ className = '' }) => {
  const { isDark, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
      className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all duration-300 group
        ${isDark
          ? 'bg-brand-blue/15 border-brand-blue/30 text-brand-blue hover:bg-brand-blue/25'
          : 'bg-amber-50 border-amber-200 text-amber-600 hover:bg-amber-100'
        } ${className}`}
    >
      <span className="transition-transform duration-300 group-hover:scale-110">
        {isDark ? <SunIcon /> : <MoonIcon />}
      </span>
      <span className="text-xs font-semibold hidden sm:inline">
        {isDark ? 'Light' : 'Dark'}
      </span>
    </button>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
      const sections = ['home', 'about', 'services', 'products', 'portfolio', 'careers', 'contact'];
      for (const id of [...sections].reverse()) {
        const el = document.getElementById(id);
        if (el && window.scrollY >= el.offsetTop - 120) { setActiveSection(id); break; }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Products', href: '#products' },
    { name: 'Portfolio', href: '#portfolio' },
    { name: 'Careers', href: '#careers' },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setIsOpen(false);
  };

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'glass border-b py-2' : 'bg-transparent py-3'}`}
      style={isScrolled ? { borderBottomColor: 'var(--glass-border)' } : {}}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 lg:h-16">

          {/* Logo */}
          <button onClick={() => scrollToSection('#home')} className="flex items-center gap-1">
            <img src={logo} alt="Avarnix" className="h-[4.9rem] sm:h-[5.67rem] lg:h-[6.93rem] w-auto -mr-2 sm:-mr-3 lg:-mr-4" />
            <div className="text-left brand-wordmark">
              <div className="brand-title text-base lg:text-lg font-bold text-brand-text leading-none">
                <span className="text-gradient">Avarnix</span> <span className="brand-title-tech">Technologies</span>
              </div>
              <div className="brand-tagline">Where Ideas Meet AI</div>
            </div>
          </button>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = activeSection === item.href.replace('#', '');
              return (
                <button key={item.name} onClick={() => scrollToSection(item.href)}
                  className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive ? 'text-brand-blue bg-brand-blue/10' : 'text-brand-muted hover:text-brand-text hover:bg-brand-border/40'
                  }`}
                >
                  {item.name}
                  {isActive && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-brand-blue rounded-full" />}
                </button>
              );
            })}
          </div>

          {/* Right: toggle + CTA + hamburger */}
          <div className="flex items-center gap-2 md:gap-3">
            <ThemeToggle />
            <button onClick={() => scrollToSection('#contact')} className="hidden lg:inline-flex btn-primary text-sm py-2 px-5">
              Get Started
            </button>
            <button onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden w-9 h-9 flex items-center justify-center glass rounded-lg text-brand-muted hover:text-brand-blue transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen
                ? <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                : <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              }
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`lg:hidden overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="glass px-4 py-4 space-y-1" style={{ borderTop: '1px solid var(--glass-border)' }}>
          {navItems.map((item) => (
            <button key={item.name} onClick={() => scrollToSection(item.href)}
              className="w-full text-left px-4 py-3 text-brand-muted hover:text-brand-blue hover:bg-brand-blue/10 rounded-lg text-sm font-medium transition-colors">
              {item.name}
            </button>
          ))}
          <div className="pt-2">
            <button onClick={() => scrollToSection('#contact')} className="w-full btn-primary text-sm py-3">Get Started</button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
